import React, { useState, useEffect, useCallback } from 'react';
import { 
  ArrowLeft, 
  RefreshCw, 
  Users, 
  TrendingUp, 
  Zap, 
  ShieldCheck, 
  BarChart3, 
  MapPin, 
  Layers, 
  Activity,
  Crown,
  Lock,
  Award
} from 'lucide-react';
import { supabase } from '../../lib/supabase';
import { PlatformManagementKPIs } from '../../types';
import { formatCurrencyBRL } from '../../utils/formatUtils';

interface PlatformManagementDashboardProps {
  onBack: () => void;
  userEmail?: string;
}

export default function PlatformManagementDashboard({ onBack, userEmail }: PlatformManagementDashboardProps) {
  const [periodDays, setPeriodDays] = useState<number>(30);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [kpis, setKpis] = useState<PlatformManagementKPIs | null>(null);
  const [lastRefreshedAt, setLastRefreshedAt] = useState<Date>(new Date());

  const fetchKPIs = useCallback(async (days: number) => {
    setIsLoading(true);
    setError(null);
    try {
      if (!supabase) {
        throw new Error('Supabase client não inicializado.');
      }

      const { data, error: rpcError } = await supabase.rpc('get_platform_management_kpis', {
        p_period_days: days
      });

      if (rpcError) {
        console.warn('Erro ao carregar KPIs da plataforma via RPC:', rpcError);
        setError(rpcError.message ? `Erro do Banco: ${rpcError.message}` : 'A função RPC ainda não foi executada no Supabase ou sua conta ainda não possui a permissão is_admin ativa.');
      } else if (data) {
        setKpis(data as PlatformManagementKPIs);
        setLastRefreshedAt(new Date());
      }
    } catch (err: any) {
      console.error('Falha ao buscar KPIs de gestão:', err);
      setError(err?.message || 'Falha na conexão com o banco de dados.');
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchKPIs(periodDays);
  }, [fetchKPIs, periodDays]);

  // Cálculos derivados
  const usersOverview = kpis?.users_overview;
  const operationalVolume = kpis?.operational_volume;
  const plans = usersOverview?.plans_distribution;
  const totalUsersCount = usersOverview?.total_users || 0;
  const topNiches = kpis?.top_niches || [];
  const topStates = kpis?.top_states || [];
  const lgpd = kpis?.lgpd_benchmarking;
  const dailyActivity = kpis?.daily_activity || [];

  const maxDailyQuotes = Math.max(...dailyActivity.map(d => d.quotes_count), 1);

  return (
    <div className="min-h-screen bg-zinc-50 text-zinc-900 p-4 sm:p-6 lg:p-8 font-sans">
      <div className="max-w-7xl mx-auto space-y-6">
        
        {/* CABEÇALHO EXECUTIVO (VERSÃO CLARA) */}
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 bg-white border border-zinc-200/80 p-5 rounded-3xl shadow-xs">
          <div className="flex items-center gap-3.5">
            <button
              type="button"
              onClick={onBack}
              className="p-2.5 bg-zinc-100 hover:bg-zinc-200 text-zinc-700 hover:text-zinc-900 rounded-2xl transition-all border border-zinc-200 cursor-pointer active:scale-95 shrink-0"
              title="Voltar ao App"
            >
              <ArrowLeft className="h-5 w-5" />
            </button>
            <div>
              <div className="flex items-center gap-2">
                <span className="px-2.5 py-0.5 rounded-full bg-amber-50 border border-amber-200 text-amber-800 font-black text-[10px] uppercase tracking-widest flex items-center gap-1">
                  <Crown className="h-3 w-3 text-amber-600" />
                  Diretoria & Gestão
                </span>
                <span className="text-zinc-400 text-xs">•</span>
                <span className="text-zinc-500 text-xs font-mono">
                  Atualizado às {lastRefreshedAt.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })}
                </span>
              </div>
              <h1 className="text-xl sm:text-2xl font-black text-zinc-900 tracking-tight mt-0.5 flex items-center gap-2">
                Torre de Controle R-MAX
              </h1>
            </div>
          </div>

          {/* FILTRO DE PERÍODO & REFRESH */}
          <div className="flex flex-wrap items-center gap-2">
            <div className="inline-flex bg-zinc-100 p-1 rounded-2xl border border-zinc-200/80">
              {[
                { label: '7 Dias', value: 7 },
                { label: '30 Dias', value: 30 },
                { label: '90 Dias', value: 90 },
                { label: 'Total (365d)', value: 365 }
              ].map((item) => (
                <button
                  key={item.value}
                  type="button"
                  onClick={() => setPeriodDays(item.value)}
                  className={`px-3 py-1.5 rounded-xl text-xs font-bold transition-all cursor-pointer ${
                    periodDays === item.value
                      ? 'bg-emerald-500 text-neutral-950 font-black shadow-xs'
                      : 'text-zinc-600 hover:text-zinc-900'
                  }`}
                >
                  {item.label}
                </button>
              ))}
            </div>

            <button
              type="button"
              onClick={() => fetchKPIs(periodDays)}
              disabled={isLoading}
              className="p-2.5 bg-zinc-100 hover:bg-zinc-200 text-zinc-700 hover:text-zinc-900 rounded-2xl border border-zinc-200 transition-all cursor-pointer active:scale-95 disabled:opacity-50"
              title="Atualizar Dados"
            >
              <RefreshCw className={`h-4 w-4 ${isLoading ? 'animate-spin text-emerald-600' : ''}`} />
            </button>
          </div>
        </div>

        {/* ALERTA INFORMATIVO CASO A RPC NÃO TENHA SIDO RODADA */}
        {error && (
          <div className="p-4 bg-amber-50 border border-amber-200 rounded-2xl text-amber-900 text-xs flex items-start gap-3 shadow-xs">
            <ShieldCheck className="h-5 w-5 text-amber-600 shrink-0 mt-0.5" />
            <div className="space-y-1">
              <p className="font-bold text-amber-950">Aviso de Inicialização da Telemetria:</p>
              <p className="text-amber-800 leading-relaxed">{error}</p>
              <p className="text-amber-900/90 text-[11px]">
                Para ativar os dados reais, basta rodar o arquivo <code>supabase/migrations/20260831150000_platform_management_kpis.sql</code> no SQL Editor do seu Supabase.
              </p>
            </div>
          </div>
        )}

        {/* LINHA 1: CARDS DE KPIS EXECUTIVOS (GRID 4 COLUNAS) */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          
          {/* CARD 1: BASE DE USUÁRIOS */}
          <div className="bg-white border border-zinc-200/80 rounded-3xl p-5 shadow-xs hover:shadow-md transition-shadow relative overflow-hidden">
            <div className="flex items-center justify-between">
              <span className="text-[11px] font-bold text-zinc-500 uppercase tracking-wider">Base de Usuários</span>
              <div className="w-9 h-9 rounded-2xl bg-blue-50 border border-blue-100 flex items-center justify-center text-blue-600">
                <Users className="h-4 w-4" />
              </div>
            </div>
            <div className="mt-3">
              <p className="text-2xl sm:text-3xl font-black text-zinc-900">
                {totalUsersCount.toLocaleString('pt-BR')}
              </p>
              <div className="flex items-center gap-1.5 mt-1 text-xs">
                <span className="text-emerald-600 font-bold flex items-center">
                  +{usersOverview?.new_users_period || 0}
                </span>
                <span className="text-zinc-500">novos no período</span>
              </div>
            </div>
            <div className="mt-4 pt-3 border-t border-zinc-100 flex items-center justify-between text-[11px] text-zinc-500">
              <span>Opt-In LGPD:</span>
              <span className="font-bold text-emerald-600">{lgpd?.opt_in_rate_percent || 0}% da base</span>
            </div>
          </div>

          {/* CARD 2: ASSINATURAS & TRIAL */}
          <div className="bg-white border border-zinc-200/80 rounded-3xl p-5 shadow-xs hover:shadow-md transition-shadow relative overflow-hidden">
            <div className="flex items-center justify-between">
              <span className="text-[11px] font-bold text-zinc-500 uppercase tracking-wider">Planos & Trials</span>
              <div className="w-9 h-9 rounded-2xl bg-emerald-50 border border-emerald-100 flex items-center justify-center text-emerald-600">
                <Zap className="h-4 w-4" />
              </div>
            </div>
            <div className="mt-3">
              <p className="text-2xl sm:text-3xl font-black text-emerald-600">
                {(plans?.trialing || 0) + (plans?.total_active_paid || 0)}
              </p>
              <p className="text-xs text-zinc-500 mt-1">
                {plans?.trialing || 0} em Test Drive (14d) • {plans?.total_active_paid || 0} Pagantes
              </p>
            </div>
            {/* Barra de composição */}
            <div className="mt-4 pt-3 border-t border-zinc-100 space-y-1.5">
              <div className="w-full bg-zinc-100 h-2 rounded-full overflow-hidden flex">
                <div 
                  className="bg-emerald-500 h-full" 
                  style={{ width: `${totalUsersCount ? ((plans?.active_specialist || 0) / totalUsersCount) * 100 : 0}%` }}
                  title="Especialista"
                />
                <div 
                  className="bg-blue-500 h-full" 
                  style={{ width: `${totalUsersCount ? ((plans?.active_pro || 0) / totalUsersCount) * 100 : 0}%` }}
                  title="Pro"
                />
                <div 
                  className="bg-amber-400 h-full" 
                  style={{ width: `${totalUsersCount ? ((plans?.trialing || 0) / totalUsersCount) * 100 : 0}%` }}
                  title="Trial 14d"
                />
              </div>
              <div className="flex justify-between text-[10px] text-zinc-500 font-semibold">
                <span className="text-emerald-700">Esp: {plans?.active_specialist || 0}</span>
                <span className="text-blue-700">Pro: {plans?.active_pro || 0}</span>
                <span className="text-amber-700">Trial: {plans?.trialing || 0}</span>
                <span>Free: {plans?.free || 0}</span>
              </div>
            </div>
          </div>

          {/* CARD 3: VOLUME DE ORÇAMENTOS */}
          <div className="bg-white border border-zinc-200/80 rounded-3xl p-5 shadow-xs hover:shadow-md transition-shadow relative overflow-hidden">
            <div className="flex items-center justify-between">
              <span className="text-[11px] font-bold text-zinc-500 uppercase tracking-wider">Orçamentos no Período</span>
              <div className="w-9 h-9 rounded-2xl bg-amber-50 border border-amber-100 flex items-center justify-center text-amber-600">
                <TrendingUp className="h-4 w-4" />
              </div>
            </div>
            <div className="mt-3">
              <p className="text-2xl sm:text-3xl font-black text-zinc-900">
                {(operationalVolume?.quotes_period || 0).toLocaleString('pt-BR')}
              </p>
              <p className="text-xs text-zinc-500 mt-1">
                Volume: <strong className="text-zinc-800">{formatCurrencyBRL(operationalVolume?.quotes_period_value || 0)}</strong>
              </p>
            </div>
            <div className="mt-4 pt-3 border-t border-zinc-100 flex items-center justify-between text-[11px] text-zinc-500">
              <span>Ticket Médio Geral:</span>
              <span className="font-bold text-zinc-900">{formatCurrencyBRL(operationalVolume?.avg_ticket || 0)}</span>
            </div>
          </div>

          {/* CARD 4: MÓDULOS ERP ATIVOS */}
          <div className="bg-white border border-zinc-200/80 rounded-3xl p-5 shadow-xs hover:shadow-md transition-shadow relative overflow-hidden">
            <div className="flex items-center justify-between">
              <span className="text-[11px] font-bold text-zinc-500 uppercase tracking-wider">Operações Especialistas</span>
              <div className="w-9 h-9 rounded-2xl bg-purple-50 border border-purple-100 flex items-center justify-center text-purple-600">
                <Layers className="h-4 w-4" />
              </div>
            </div>
            <div className="mt-3 grid grid-cols-3 gap-2 text-center">
              <div className="bg-zinc-50 p-2 rounded-xl border border-zinc-100">
                <p className="text-base font-black text-purple-700">{operationalVolume?.work_orders_period || 0}</p>
                <p className="text-[9px] text-zinc-500 uppercase font-bold">OS Emitidas</p>
              </div>
              <div className="bg-zinc-50 p-2 rounded-xl border border-zinc-100">
                <p className="text-base font-black text-teal-700">{operationalVolume?.appointments_period || 0}</p>
                <p className="text-[9px] text-zinc-500 uppercase font-bold">Agendamentos</p>
              </div>
              <div className="bg-zinc-50 p-2 rounded-xl border border-zinc-100">
                <p className="text-base font-black text-emerald-700">{operationalVolume?.receipts_period || 0}</p>
                <p className="text-[9px] text-zinc-500 uppercase font-bold">Recibos PIX</p>
              </div>
            </div>
            <div className="mt-4 pt-3 border-t border-zinc-100 flex items-center justify-between text-[11px] text-zinc-500">
              <span>Histórico Total Acumulado:</span>
              <span className="font-bold text-zinc-800">{(operationalVolume?.total_quotes_ever || 0).toLocaleString('pt-BR')} orçamentos</span>
            </div>
          </div>

        </div>

        {/* LINHA 2: TENDÊNCIA TEMPORAL DIÁRIA */}
        {dailyActivity.length > 0 && (
          <div className="bg-white border border-zinc-200/80 rounded-3xl p-6 shadow-xs space-y-4">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="text-sm font-bold text-zinc-900 flex items-center gap-2">
                  <Activity className="h-4 w-4 text-emerald-600" />
                  Evolução Diária de Orçamentos Gerados
                </h3>
                <p className="text-xs text-zinc-500">Volume diário de cotações emitidas por prestadores na plataforma</p>
              </div>
            </div>

            {/* Gráfico de Barras CSS Responsivo */}
            <div className="pt-4 flex items-end gap-1.5 sm:gap-2 h-44 overflow-x-auto pb-2">
              {dailyActivity.map((day, idx) => {
                const heightPercent = maxDailyQuotes > 0 ? (day.quotes_count / maxDailyQuotes) * 100 : 0;
                return (
                  <div key={idx} className="flex-1 min-w-5 flex flex-col items-center gap-1.5 group h-full justify-end">
                    <div className="text-[9px] font-mono text-zinc-500 opacity-0 group-hover:opacity-100 transition-opacity">
                      {day.quotes_count}
                    </div>
                    <div 
                      className="w-full bg-linear-to-t from-emerald-500 to-teal-400 hover:from-emerald-600 hover:to-teal-500 rounded-t-md transition-all cursor-pointer"
                      style={{ height: `${Math.max(heightPercent, 4)}%` }}
                      title={`${day.label}: ${day.quotes_count} orçamentos (${formatCurrencyBRL(day.total_value)})`}
                    />
                    <span className="text-[9px] text-zinc-400 font-mono tracking-tighter truncate w-full text-center">
                      {day.label}
                    </span>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* LINHA 3: TOP NICHOS E MAPA REGIONAL (GRID 2 COLUNAS) */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          
          {/* TOP NICHOS */}
          <div className="bg-white border border-zinc-200/80 rounded-3xl p-6 shadow-xs space-y-4">
            <div className="flex items-center justify-between border-b border-zinc-100 pb-3">
              <h3 className="text-sm font-bold text-zinc-900 flex items-center gap-2">
                <BarChart3 className="h-4 w-4 text-blue-600" />
                Top Nichos com Maior Tração
              </h3>
              <span className="text-xs text-zinc-500">Por volume de cotações</span>
            </div>

            {topNiches.length === 0 ? (
              <p className="text-xs text-zinc-400 py-6 text-center">Nenhum orçamento registrado no período selecionado.</p>
            ) : (
              <div className="space-y-3">
                {topNiches.map((niche, idx) => {
                  const maxQuotes = topNiches[0]?.quotes_count || 1;
                  const percent = Math.round((niche.quotes_count / maxQuotes) * 100);
                  return (
                    <div key={idx} className="p-3 bg-zinc-50 border border-zinc-100 rounded-2xl space-y-1.5">
                      <div className="flex items-center justify-between text-xs">
                        <span className="font-bold text-zinc-800 flex items-center gap-2">
                          <span className="w-5 h-5 rounded-full bg-zinc-200 text-[10px] font-mono flex items-center justify-center text-zinc-600">
                            #{idx + 1}
                          </span>
                          {niche.name}
                        </span>
                        <div className="text-right">
                          <span className="font-black text-zinc-900">{niche.quotes_count} orçamentos</span>
                          <span className="text-zinc-500 text-[11px] ml-1.5 font-mono">({formatCurrencyBRL(niche.total_value)})</span>
                        </div>
                      </div>
                      <div className="w-full bg-zinc-200 h-1.5 rounded-full overflow-hidden">
                        <div className="bg-blue-600 h-full rounded-full" style={{ width: `${percent}%` }} />
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {/* DISTRIBUIÇÃO REGIONAL */}
          <div className="bg-white border border-zinc-200/80 rounded-3xl p-6 shadow-xs space-y-4">
            <div className="flex items-center justify-between border-b border-zinc-100 pb-3">
              <h3 className="text-sm font-bold text-zinc-900 flex items-center gap-2">
                <MapPin className="h-4 w-4 text-emerald-600" />
                Presença por Estado (UFs)
              </h3>
              <span className="text-xs text-zinc-500">Prestadores & Cotações</span>
            </div>

            {topStates.length === 0 ? (
              <p className="text-xs text-zinc-400 py-6 text-center">Nenhum dado regional registrado no período.</p>
            ) : (
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-2.5">
                {topStates.map((stateItem, idx) => (
                  <div key={idx} className="p-3 bg-zinc-50 border border-zinc-100 rounded-2xl text-center space-y-1">
                    <div className="inline-flex items-center justify-center w-8 h-8 rounded-xl bg-emerald-50 text-emerald-700 font-black text-xs border border-emerald-200">
                      {stateItem.uf}
                    </div>
                    <p className="text-xs font-black text-zinc-900">{stateItem.quotes_count} orçamentos</p>
                    <p className="text-[10px] text-zinc-500">{stateItem.users_count} prestadores</p>
                  </div>
                ))}
              </div>
            )}

            {/* Box Informativo de Benchmarking */}
            <div className="mt-4 p-3.5 bg-emerald-50 border border-emerald-100 rounded-2xl flex items-start gap-2.5 text-xs text-emerald-900">
              <ShieldCheck className="h-4 w-4 text-emerald-600 shrink-0 mt-0.5" />
              <div>
                <p className="font-bold text-emerald-950">Regra de K-Anonimato (k ≥ 30):</p>
                <p className="text-[11px] text-emerald-800 leading-relaxed">
                  Estados com mais de 30 prestadores ativos alimentam automaticamente o dataset materializado de inteligência setorial, sem risco de reidentificação.
                </p>
              </div>
            </div>
          </div>

        </div>

      </div>
    </div>
  );
}
